<script>
import { getDashboard } from '@/api/dashboard.js'

export default {
  name: 'DashboardPage',
  data() {
    return {
      loading: false,
      dash: null,
    }
  },
  computed: {
    todayStr() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await getDashboard()
        if (res.data?.code === 0) {
          this.dash = res.data.data || null
        }
      } catch (e) {
        console.error('[DashboardPage] load', e)
      } finally {
        this.loading = false
      }
    },
    fmtNum(n) {
      if (n == null || n === '') return '0'
      return new Intl.NumberFormat('ko-KR').format(Number(n))
    },
    fmtWon(n) {
      if (n == null || n === '') return '-'
      return `${new Intl.NumberFormat('ko-KR').format(Number(n))}원`
    },
    spotLabel(s) {
      if (s === 'GOLD') return '금'
      if (s === 'SILVER') return '은'
      return s || '-'
    },
    formatDt(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
    orderTodayQuery() {
      return {
        path: '/order-status',
        query: {
          startDate: this.todayStr,
          endDate: this.todayStr,
        },
      }
    },
    goSettlement(row) {
      if (row.orderItemCode) {
        this.$router.push(this.orderTodayQuery())
      }
    },
    goMemberFromInquiry(row) {
      if (row.userCode) this.$router.push(`/members/${row.userCode}`)
    },
    goWorkShare(row) {
      if (row.workSharePostCode) this.$router.push(`/work-share/${row.workSharePostCode}`)
    },
    goStorageRequest(row) {
      if (row.storageRequestCode) this.$router.push(`/storage/requests/${row.storageRequestCode}`)
      else this.$router.push('/storage')
    },
    storageStatusLabel(value) {
      return {
        DELIVERY_REQUESTED: '배송신청',
        TRANSFERRING: '이동중',
        READY_FOR_PICKUP: '수령준비',
        SHIPPING: '배송중',
      }[value] || value || '-'
    },
    workStatusLabel(value) {
      return { OPEN: '대기', CONFIRMED: '확인', IN_PROGRESS: '진행중', DONE: '완료', HOLD: '보류' }[value] || value || '-'
    },
    statusSummary(row) {
      const parts = [
        { label: '대기', value: row.openCount || 0, color: 'secondary' },
        { label: '확인', value: row.confirmedCount || 0, color: 'primary' },
        { label: '완료', value: row.doneCount || 0, color: 'success' },
        { label: '보류', value: row.holdCount || 0, color: 'grey' },
      ].filter(v => v.value > 0)
      if (!parts.length) return [{ label: this.workStatusLabel(row.workStatus), value: null, color: 'primary' }]
      return parts
    },
    priorityLabel(value) {
      return { NORMAL: '보통', IMPORTANT: '중요', URGENT: '긴급' }[value] || value || '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
      <div>
        <div class="text-h5 font-weight-bold mb-1">대시보드</div>
        <div class="text-body-2 text-grey">주문, 결제, 문의 현황을 요약합니다.</div>
      </div>
      <v-btn variant="tonal" color="primary" size="small" :loading="loading" @click="load">
        새로고침
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="mb-4" />

    <template v-if="dash">
      <div class="storage-dashboard-panel mb-8" @click="$router.push('/storage')">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
          <div>
            <div class="text-subtitle-1 font-weight-bold text-white">보관/이동 관리</div>
            <div class="text-caption text-blue-grey-lighten-3">수령/배송 신청과 지점 이동이 필요한 보관 상품입니다.</div>
          </div>
          <v-btn variant="tonal" color="warning" size="small" to="/storage" @click.stop>전체보기</v-btn>
        </div>
        <v-row>
          <v-col cols="6" sm="4" md="2">
            <div class="storage-dashboard-stat"><span>보관중</span><strong>{{ fmtNum(dash.storageSummary?.storedCount) }}</strong></div>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <div class="storage-dashboard-stat active"><span>신청</span><strong>{{ fmtNum(dash.storageSummary?.requestCount) }}</strong></div>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <div class="storage-dashboard-stat active"><span>이동중</span><strong>{{ fmtNum(dash.storageSummary?.transferringCount) }}</strong></div>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <div class="storage-dashboard-stat"><span>수령준비</span><strong>{{ fmtNum(dash.storageSummary?.readyForPickupCount) }}</strong></div>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <div class="storage-dashboard-stat"><span>배송중</span><strong>{{ fmtNum(dash.storageSummary?.shippingCount) }}</strong></div>
          </v-col>
        </v-row>
        <div v-if="(dash.storageUrgentList || []).length" class="storage-urgent-list mt-4">
          <button
            v-for="row in dash.storageUrgentList"
            :key="row.storageItemCode"
            class="storage-urgent-item"
            type="button"
            @click.stop="goStorageRequest(row)"
          >
            <span class="storage-urgent-title">
              {{ row.targetBranchName || row.currentBranchName || '-' }}에 방문 수령을 위한 제품 이동 요청이 왔습니다.
            </span>
            <span class="storage-urgent-meta">
              {{ storageStatusLabel(row.storageStatus) }} · {{ row.currentBranchName || '-' }} → {{ row.targetBranchName || '-' }} · {{ row.userName }} · {{ row.productName }}
            </span>
          </button>
        </div>
      </div>
      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">업무 공유</div>
      <v-row class="mb-8">
        <v-col cols="12">
          <v-card rounded="lg" elevation="1">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">최근 업무공유게시판</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/work-share">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>중요도</th>
                  <th>상태</th>
                  <th>제목</th>
                  <th>대상</th>
                  <th>작성자</th>
                  <th>종료기한</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in (dash.recentWorkShares || [])"
                  :key="row.workSharePostCode"
                  style="cursor:pointer"
                  @click="goWorkShare(row)"
                >
                  <td><v-chip size="small" variant="tonal">{{ priorityLabel(row.priorityType) }}</v-chip></td>
                  <td>
                    <div class="status-summary">
                      <v-chip
                        v-for="item in statusSummary(row)"
                        :key="`${row.workSharePostCode}-${item.label}`"
                        size="small"
                        :color="item.color"
                        variant="tonal"
                      >
                        {{ item.value === null ? item.label : `${item.label} ${item.value}` }}
                      </v-chip>
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-bold">{{ row.boardTitle }}</div>
                    <div class="text-caption text-grey">댓글 {{ row.commentCount || 0 }} · 첨부 {{ row.attachmentCount || 0 }}</div>
                  </td>
                  <td class="text-caption">{{ row.targetBranchNames || row.targetAdminNames || '전체 지점' }}</td>
                  <td>{{ row.writerName || '-' }}</td>
                  <td class="text-caption">{{ formatDt(row.dueDt) }}</td>
                </tr>
                <tr v-if="!(dash.recentWorkShares || []).length">
                  <td colspan="6" class="text-center text-grey py-4">최근 업무 공유 게시글이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>
      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">오늘 요약</div>
      <v-row class="mb-8">
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">오늘 주문</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" :to="orderTodayQuery()">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>금 주문</span><span>{{ fmtNum(dash.productBuyToday?.goldTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>금 주문금액</span><span>{{ fmtWon(dash.productBuyToday?.goldAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between"><span>은 주문</span><span>{{ fmtNum(dash.productBuyToday?.silverTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>은 주문금액</span><span>{{ fmtWon(dash.productBuyToday?.silverAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 건수</span><span>{{ fmtNum(dash.productBuyToday?.totalTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 금액</span><span>{{ fmtWon(dash.productBuyToday?.totalAmountSum) }}</span></div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="dash.cashTodayHq" cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100 dashboard-hq-cash dashboard-hq-cash--in">
            <v-card-title class="text-subtitle-1 font-weight-bold">오늘 현금 입금</v-card-title>
            <v-card-text class="text-body-2">
              <div class="text-h6 font-weight-bold text-success mb-2">
                {{ fmtWon(dash.cashTodayHq.depositAmountToday) }}
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-grey">건수</span>
                <span>{{ fmtNum(dash.cashTodayHq.depositCountToday) }}건</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="dash.cashTodayHq" cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100 dashboard-hq-cash dashboard-hq-cash--out">
            <v-card-title class="text-subtitle-1 font-weight-bold">오늘 취소/환불</v-card-title>
            <v-card-text class="text-body-2">
              <div class="text-h6 font-weight-bold text-error mb-2">
                {{ fmtWon(dash.cashTodayHq.withdrawAmountToday) }}
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-grey">건수</span>
                <span>{{ fmtNum(dash.cashTodayHq.withdrawCountToday) }}건</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">오늘 문의</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/inquiries">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>신규 문의</span><span>{{ fmtNum(dash.inquiryToday?.questionCountToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>답변</span><span>{{ fmtNum(dash.inquiryToday?.answerCountToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>미답변</span><span>{{ fmtNum(dash.inquiryToday?.unansweredOpenCount) }}건</span></div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template v-if="dash.hq && (dash.membersHq || dash.branchesHq)">
        <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">본사 관리</div>
        <v-row class="mb-8">
          <v-col v-if="dash.membersHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100">
              <v-card-item>
                <v-card-title class="text-subtitle-1 font-weight-bold">회원</v-card-title>
                <template #append>
                  <v-btn variant="text" size="small" color="primary" to="/members">전체보기</v-btn>
                </template>
              </v-card-item>
              <v-card-text class="text-body-2">
                <div class="d-flex justify-space-between"><span>전체 회원</span><span>{{ fmtNum(dash.membersHq.totalMembers) }}명</span></div>
                <div class="d-flex justify-space-between"><span>오늘 가입</span><span>{{ fmtNum(dash.membersHq.joinedToday) }}명</span></div>
                <div class="d-flex justify-space-between"><span>이번 주 가입</span><span>{{ fmtNum(dash.membersHq.joinedThisWeek) }}명</span></div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="dash.branchesHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100">
              <v-card-item>
                <v-card-title class="text-subtitle-1 font-weight-bold">지점</v-card-title>
                <template #append>
                  <v-btn variant="text" size="small" color="primary" to="/branches">전체보기</v-btn>
                </template>
              </v-card-item>
              <v-card-text class="text-body-2">
                <div class="d-flex justify-space-between"><span>전체 지점</span><span>{{ fmtNum(dash.branchesHq.totalBranches) }}개</span></div>
                <div class="d-flex justify-space-between"><span>최근 30일 신규</span><span>{{ fmtNum(dash.branchesHq.newBranchesLast30Days) }}개</span></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">오늘 주문 내역</div>
      <v-row class="mb-8">
        <v-col cols="12">
          <v-card rounded="lg" elevation="1">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">최근 주문</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" :to="orderTodayQuery()">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>회원</th>
                  <th>현물</th>
                  <th class="text-end">중량</th>
                  <th class="text-end">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in (dash.productBuyTodayList || [])"
                  :key="row.orderItemCode"
                  style="cursor:pointer"
                  @click="goSettlement(row)"
                >
                  <td class="text-caption">{{ formatDt(row.orderedAt) }}</td>
                  <td>{{ row.buyerName || '-' }}</td>
                  <td>{{ spotLabel(row.spotType) }}</td>
                  <td class="text-end text-caption">{{ fmtNum(row.weightG) }}g</td>
                  <td class="text-end text-caption">{{ fmtWon(row.orderAmount) }}</td>
                </tr>
                <tr v-if="!(dash.productBuyTodayList || []).length">
                  <td colspan="5" class="text-center text-grey py-4">주문 내역 없음</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>

      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">최근 문의</div>
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card rounded="lg" elevation="1">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">미답변 문의</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/inquiries">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-list density="compact" lines="two" class="dashboard-inquiry-list">
              <v-list-item
                v-for="row in (dash.recentInquiries || [])"
                :key="row.boardCode + row.userCode"
                :title="row.userName || row.userCode || '-'"
                :subtitle="(row.boardTitle || '') + (row.previewText ? ' - ' + row.previewText : '')"
                @click="goMemberFromInquiry(row)"
              >
                <template #append>
                  <span class="text-caption text-grey">{{ formatDt(row.lastQuestionAt) }}</span>
                </template>
              </v-list-item>
              <v-list-item v-if="!(dash.recentInquiries || []).length">
                <v-list-item-title class="text-grey">문의 없음</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-alert v-else-if="!loading" type="info" variant="tonal" class="mt-4">
      대시보드 데이터를 불러오지 못했습니다.
    </v-alert>
  </div>
</template>

<style scoped>
.dashboard-hq-cash {
  border-inline-start: 3px solid #e0e0e0;
}

.dashboard-hq-cash--in {
  border-inline-start-color: #2e7d32;
}

.dashboard-hq-cash--out {
  border-inline-start-color: #c62828;
}

.dashboard-inquiry-list {
  background: transparent !important;
}

.dashboard-inquiry-list :deep(.v-list-item) {
  background: transparent !important;
  border-bottom: 1px solid rgba(71, 85, 105, 0.22);
}

.dashboard-inquiry-list :deep(.v-list-item:last-child) {
  border-bottom: 0;
}

.status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.storage-dashboard-panel {
  padding: 20px;
  border: 1px solid rgba(215, 184, 109, 0.55);
  border-radius: 14px;
  background: linear-gradient(135deg, #151c2a 0%, #202a3b 100%);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.storage-dashboard-stat {
  min-height: 84px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.045);
}

.storage-dashboard-stat span {
  display: block;
  color: #aeb8c8;
  font-size: 13px;
}

.storage-dashboard-stat strong {
  display: block;
  margin-top: 8px;
  color: #fff;
  font-size: 28px;
  line-height: 1;
}

.storage-dashboard-stat.active {
  border-color: rgba(215, 184, 109, 0.8);
}

.storage-dashboard-stat.active strong {
  color: #d7b86d;
}

.storage-urgent-list {
  display: grid;
  gap: 8px;
}

.storage-urgent-item {
  display: grid;
  width: 100%;
  gap: 4px;
  padding: 12px 14px;
  text-align: left;
  color: #fff;
  border: 1px solid rgba(215, 184, 109, 0.35);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.16);
}

.storage-urgent-item:hover {
  border-color: rgba(215, 184, 109, 0.9);
  background: rgba(215, 184, 109, 0.1);
}

.storage-urgent-title {
  font-size: 14px;
  font-weight: 800;
}

.storage-urgent-meta {
  color: #aeb8c8;
  font-size: 12px;
}
</style>
